/**
 * Willoah Growth glyphs — small inline SVGs.
 *
 * Style rules (per Feb 22 spec):
 *   - Thin line only (stroke=1.5), monochrome, letterpress feel.
 *   - No gradients, no shadows, no cartoon rounding.
 *   - Uses currentColor so callers can tint via Tailwind text-* utilities.
 *   - viewBox 0 0 24 24 so they slot cleanly next to lucide icons.
 *
 * Sun glyph variants (per Feb 22 revision — "current version feels clinical"):
 *   - v1 "Petal Sun": rays are short curved petals radiating out (organic,
 *     leaf-like — feels closest to How-It-Works botanical linework)
 *   - v2 "Uneven Rays": alternating short/long straight rays with softly
 *     rounded caps and a small off-center dot (letterpress imperfection)
 *   - v3 "Sprig Sun": mixed rays and tiny leaf-buds around the center (most
 *     charming, most obviously botanical)
 *
 * The active variant is chosen via `PREFERRED_SUN_GLYPH` below — a single
 * constant makes it a two-line change to swap after a look-and-feel review.
 * The Growth Stage glyphs get progressively more plant-like:
 *   sprout -> tiny arc pushing up
 *   growing -> a stem with two leaves
 *   bloomed -> stem, leaves, and a single simple bloom
 *   deep_roots -> plant + downward roots below the baseline
 */
import React from "react";

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  // Thin-line letterpress feel — matches How-It-Works icons. Deliberately
  // lighter than lucide's default 1.75 so the Sun doesn't read as bold
  // next to the other action icons.
  strokeWidth: 1.15,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

// ---- Sun glyph variants -----------------------------------------------
// Pick which variant renders across the site (kept as a single constant so
// swapping after a review is a one-line edit). See SunGlyphV1/V2/V3 below.
export const PREFERRED_SUN_GLYPH = "v1"; // "v1" | "v2" | "v3"

/**
 * v1 — Petal Sun with a smile. Center dot + short curved rays that read as
 * tiny leaves or petals. Two dot eyes + a subtle smile curve. Uneven angles
 * = handmade, letterpress feel.
 *
 * Filled state (Kelly's Feb 22 revision): the tap must FEEL like sunlight
 * arrived — so we (1) fill the center with a warm honey, (2) add a soft
 * halo ring behind the sun, (3) draw the smile with a heavier stroke so
 * the face reads as clearly happy.
 */
export function SunGlyphV1({ filled = false, className = "", ...rest }) {
  const HONEY = "#E4C078";
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" className={className} aria-hidden="true" {...rest}>
      {/* Soft halo ring — only appears when tapped. Reads as a subtle glow
          behind the sun without needing SVG filters (which don't animate
          reliably on mobile). */}
      {filled && (
        <circle
          cx="12"
          cy="12"
          r="7.8"
          fill={HONEY}
          fillOpacity="0.28"
          stroke="none"
        />
      )}
      {/* Center — outlined outline stays visible; when filled we drop a
          warm honey fill inside so the sun visibly "lights up". */}
      <circle
        cx="12"
        cy="12"
        r="3.6"
        {...strokeProps}
        fill={filled ? HONEY : "none"}
        fillOpacity={filled ? 0.85 : 0}
      />
      {/* Face — tiny dot eyes + a soft smile curve. When lit up the smile
          gets a slightly heavier stroke so the whole face reads happier. */}
      <circle cx="10.6" cy="11.3" r="0.55" fill="currentColor" stroke="none" />
      <circle cx="13.4" cy="11.3" r="0.55" fill="currentColor" stroke="none" />
      <path
        d="M 10.5 12.9 Q 12 14 13.5 12.9"
        fill="none"
        stroke="currentColor"
        strokeWidth={filled ? 1.5 : 1.15}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Eight small petal-like curved rays — quadratic curves, not straight
          lines — with slight angle offsets so the sun feels hand-drawn. */}
      <path d="M12 4.6 C 12.7 5.8 11.3 6.6 12 7.4" {...strokeProps} />
      <path d="M12 19.4 C 11.3 18.2 12.7 17.4 12 16.6" {...strokeProps} />
      <path d="M4.6 12 C 5.8 11.3 6.6 12.7 7.4 12" {...strokeProps} />
      <path d="M19.4 12 C 18.2 12.7 17.4 11.3 16.6 12" {...strokeProps} />
      <path d="M6.2 6.0 C 7.2 6.6 7.8 7.4 8.4 8.3" {...strokeProps} />
      <path d="M17.8 18.0 C 16.8 17.4 16.2 16.6 15.6 15.7" {...strokeProps} />
      <path d="M6.0 17.8 C 6.6 16.8 7.4 16.4 8.3 15.6" {...strokeProps} />
      <path d="M17.8 6.0 C 17.2 7.0 16.4 7.4 15.7 8.3" {...strokeProps} />
    </svg>
  );
}

/**
 * v2 — Uneven Rays. Straight rays but alternating short/long with a small
 * off-center dot in the middle. Reads like an old woodblock print.
 */
export function SunGlyphV2({ filled = false, className = "", ...rest }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" className={className} aria-hidden="true" {...rest}>
      <circle
        cx="12"
        cy="12"
        r="3.6"
        {...strokeProps}
        fill={filled ? "currentColor" : "none"}
        fillOpacity={filled ? 0.22 : 0}
      />
      {/* Tiny asymmetric dot inside — the "imperfection" that makes it feel
          hand-drawn, not clip-art. */}
      <circle cx="12.7" cy="11.3" r="0.55" fill="currentColor" stroke="none" />
      {/* Rays: alternating length. Even indices (N,S,E,W) longer; diagonals
          shorter — creates rhythm. */}
      <line x1="12" y1="2.5" x2="12" y2="5.4" {...strokeProps} />
      <line x1="12" y1="18.6" x2="12" y2="21.5" {...strokeProps} />
      <line x1="2.5" y1="12" x2="5.4" y2="12" {...strokeProps} />
      <line x1="18.6" y1="12" x2="21.5" y2="12" {...strokeProps} />
      <line x1="5.6" y1="5.6" x2="6.9" y2="6.9" {...strokeProps} />
      <line x1="17.1" y1="17.1" x2="18.4" y2="18.4" {...strokeProps} />
      <line x1="5.6" y1="18.4" x2="6.9" y2="17.1" {...strokeProps} />
      <line x1="17.1" y1="6.9" x2="18.4" y2="5.6" {...strokeProps} />
    </svg>
  );
}

/**
 * v3 — Sprig Sun. Alternating rays and tiny leaf-buds around the center.
 * Most explicitly botanical of the three.
 */
export function SunGlyphV3({ filled = false, className = "", ...rest }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" className={className} aria-hidden="true" {...rest}>
      <circle
        cx="12"
        cy="12"
        r="3"
        {...strokeProps}
        fill={filled ? "currentColor" : "none"}
        fillOpacity={filled ? 0.3 : 0}
      />
      {/* Straight rays on the 4 cardinal points… */}
      <line x1="12" y1="3.5" x2="12" y2="6.5" {...strokeProps} />
      <line x1="12" y1="17.5" x2="12" y2="20.5" {...strokeProps} />
      <line x1="3.5" y1="12" x2="6.5" y2="12" {...strokeProps} />
      <line x1="17.5" y1="12" x2="20.5" y2="12" {...strokeProps} />
      {/* …and tiny curved leaf-buds on the diagonals. Each is a two-arc
          teardrop pointing outward from the center. */}
      <path d="M6 6 C 7 5, 7.8 5, 8.4 5.6 C 8 6.2, 7.4 6.6, 6 6 Z" {...strokeProps} />
      <path d="M18 18 C 17 19, 16.2 19, 15.6 18.4 C 16 17.8, 16.6 17.4, 18 18 Z" {...strokeProps} />
      <path d="M6 18 C 5 17, 5 16.2, 5.6 15.6 C 6.2 16, 6.6 16.6, 6 18 Z" {...strokeProps} />
      <path d="M18 6 C 19 7, 19 7.8, 18.4 8.4 C 17.8 8, 17.4 7.4, 18 6 Z" {...strokeProps} />
    </svg>
  );
}

/**
 * Chosen Sun glyph — reads the PREFERRED_SUN_GLYPH constant so all callers
 * automatically pick up the active look.
 */
export function SunGlyph(props) {
  if (PREFERRED_SUN_GLYPH === "v2") return <SunGlyphV2 {...props} />;
  if (PREFERRED_SUN_GLYPH === "v3") return <SunGlyphV3 {...props} />;
  return <SunGlyphV1 {...props} />;
}

// Growth stage glyphs — each ~16-18px.
export function SproutGlyph({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" className={className} aria-hidden="true">
      {/* Ground line + a single sprout arc pushing up */}
      <line x1="4" y1="19" x2="20" y2="19" {...strokeProps} />
      <path d="M12 19 C 12 14, 12 12, 12 10" {...strokeProps} />
      <path d="M12 12 C 14 10, 16 10, 15 8" {...strokeProps} />
    </svg>
  );
}

export function GrowingGlyph({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" className={className} aria-hidden="true">
      <line x1="4" y1="19" x2="20" y2="19" {...strokeProps} />
      {/* stem */}
      <path d="M12 19 L 12 6" {...strokeProps} />
      {/* left leaf */}
      <path d="M12 13 C 9 12, 7 13, 7 11 C 9 10, 11 10, 12 12" {...strokeProps} />
      {/* right leaf */}
      <path d="M12 9 C 15 8, 17 9, 17 7 C 15 6, 13 6, 12 8" {...strokeProps} />
    </svg>
  );
}

export function BloomedGlyph({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" className={className} aria-hidden="true">
      <line x1="4" y1="19" x2="20" y2="19" {...strokeProps} />
      {/* stem */}
      <path d="M12 19 L 12 8" {...strokeProps} />
      {/* leaves */}
      <path d="M12 15 C 9 14, 7 15, 7 13 C 9 12, 11 12, 12 14" {...strokeProps} />
      <path d="M12 12 C 15 11, 17 12, 17 10 C 15 9, 13 9, 12 11" {...strokeProps} />
      {/* bloom — 5 short petals around a small center */}
      {[0, 72, 144, 216, 288].map((deg) => (
        <line
          key={deg}
          x1="12"
          y1="8"
          x2={12 + Math.sin((deg * Math.PI) / 180) * 3.2}
          y2={8 - Math.cos((deg * Math.PI) / 180) * 3.2}
          {...strokeProps}
        />
      ))}
      <circle cx="12" cy="8" r="1" {...strokeProps} fill="currentColor" fillOpacity="0.5" />
    </svg>
  );
}

export function DeepRootsGlyph({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" className={className} aria-hidden="true">
      {/* Ground line — a little dashed for texture */}
      <line x1="3" y1="13" x2="21" y2="13" {...strokeProps} strokeDasharray="2 2" />
      {/* stem (above ground) */}
      <path d="M12 13 L 12 4" {...strokeProps} />
      {/* leaves */}
      <path d="M12 9 C 9 8, 7 9, 7 7 C 9 6, 11 6, 12 8" {...strokeProps} />
      <path d="M12 6 C 15 5, 17 6, 17 4 C 15 3, 13 3, 12 5" {...strokeProps} />
      {/* roots below */}
      <path d="M12 13 L 12 21" {...strokeProps} />
      <path d="M12 15 L 8 20" {...strokeProps} />
      <path d="M12 15 L 16 20" {...strokeProps} />
      <path d="M12 18 L 6 21" {...strokeProps} />
      <path d="M12 18 L 18 21" {...strokeProps} />
    </svg>
  );
}

export function GrowthStageGlyph({ stage, className = "" }) {
  const map = {
    sprout: SproutGlyph,
    growing: GrowingGlyph,
    bloomed: BloomedGlyph,
    deep_roots: DeepRootsGlyph,
  };
  const G = map[stage] || SproutGlyph;
  return <G className={className} />;
}
